import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import loyaltyService from '../../../services/loyaltyService';

const TransactionHistory = ({ userId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const ITEMS_PER_PAGE = 20;

  // Load transactions
  useEffect(() => {
    loadTransactions(true);
  }, [userId, filter, sortOrder]);

  const loadTransactions = async (reset = false) => {
    if (!userId) return;
    
    const currentPage = reset ? 0 : page;
    setLoading(true);
    if (reset) setError(null);
    
    try {
      const { data, error } = await loyaltyService?.getTransactionHistory(
        userId,
        ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      );
      
      if (error) {
        setError(error?.message || 'Failed to load transactions');
      } else {
        const newTransactions = data || [];
        
        if (reset) {
          setTransactions(newTransactions);
        } else {
          setTransactions(prev => [...prev, ...newTransactions]);
        }
        
        setHasMore(newTransactions?.length === ITEMS_PER_PAGE);
        setPage(reset ? 1 : currentPage + 1);
      }
    } catch (err) {
      setError('Failed to connect to transaction service');
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions
  const filteredTransactions = transactions?.filter(transaction => {
    if (filter === 'all') return true;
    if (filter === 'earned') return transaction?.amount > 0;
    if (filter === 'spent') return transaction?.amount < 0;
    return transaction?.transaction_type === filter;
  });

  // Sort transactions
  const sortedTransactions = [...filteredTransactions]?.sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Get transaction icon and color
  const getTransactionDisplay = (transaction) => {
    const isEarned = transaction?.amount > 0;
    const icons = {
      'earned_activity': 'Activity',
      'earned_goal': 'Target',
      'earned_streak': 'Flame',
      'earned_challenge': 'Trophy',
      'earned_referral': 'Users',
      'spent_reward': 'Gift',
      'spent_marketplace': 'ShoppingCart',
      'bonus': 'Sparkles',
      'penalty': 'Minus',
      'refund': 'RefreshCw',
      'admin_adjustment': 'Settings'
    };

    return {
      icon: icons?.[transaction?.transaction_type] || (isEarned ? 'Plus' : 'Minus'),
      color: isEarned ? 'text-success' : 'text-destructive',
      bgColor: isEarned ? 'bg-success/20' : 'bg-destructive/20'
    };
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date?.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 24 * 7) {
      return date?.toLocaleDateString('en-US', { 
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit'
      });
    } else {
      return date?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date?.getFullYear() !== now?.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Group transactions by date
  const groupTransactionsByDate = (transactions) => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);
    
    transactions?.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const dateKey = date?.toDateString();
      
      let groupLabel;
      if (dateKey === today?.toDateString()) {
        groupLabel = 'Today';
      } else if (dateKey === yesterday?.toDateString()) {
        groupLabel = 'Yesterday';
      } else {
        groupLabel = date?.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: date?.getFullYear() !== today?.getFullYear() ? 'numeric' : undefined
        });
      }
      
      if (!groups?.[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups?.[groupLabel]?.push(transaction);
    });
    
    return groups;
  };

  const transactionGroups = groupTransactionsByDate(sortedTransactions);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-foreground">Filter:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Transactions</option>
              <option value="earned">Earned Only</option>
              <option value="spent">Spent Only</option>
              <option value="earned_activity">Activity Rewards</option>
              <option value="earned_goal">Goal Achievements</option>
              <option value="earned_streak">Streak Bonuses</option>
              <option value="spent_reward">Reward Redemptions</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-foreground">Sort:</span>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="flex items-center space-x-1 px-3 py-2 border border-border rounded-lg bg-card text-foreground hover:bg-muted/50 transition-colors"
            >
              <Icon name={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'} size={16} />
              <span className="text-sm">Date</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center space-x-6 text-sm">
          <div className="text-center">
            <p className="font-medium text-success">
              +{loyaltyService?.formatTokens(
                filteredTransactions?.filter(t => t?.amount > 0)?.reduce((sum, t) => sum + t?.amount, 0)
              )}
            </p>
            <p className="text-xs text-muted-foreground">Earned</p>
          </div>
          <div className="text-center">
            <p className="font-medium text-destructive">
              {loyaltyService?.formatTokens(
                filteredTransactions?.filter(t => t?.amount < 0)?.reduce((sum, t) => sum + t?.amount, 0)
              )}
            </p>
            <p className="text-xs text-muted-foreground">Spent</p>
          </div>
        </div>
      </div>
      {/* Error State */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">Error loading transactions</p>
            <p className="text-sm text-destructive/80">{error}</p>
          </div>
          <button
            onClick={() => loadTransactions(true)}
            className="ml-auto px-3 py-1 bg-destructive text-destructive-foreground rounded text-sm hover:bg-destructive/90 transition-colors"
          >
            Retry
          </button>
        </div>
      )}
      {/* Transaction Groups */}
      {Object.keys(transactionGroups)?.length === 0 && !loading ? (
        <div className="text-center py-12">
          <Icon name="Clock" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
          <p className="text-muted-foreground">
            {filter !== 'all' ? 'Try adjusting your filter settings' : 'Complete activities to start earning tokens!'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(transactionGroups)?.map(([groupLabel, groupTransactions]) => (
            <div key={groupLabel}>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 sticky top-0 bg-background py-2">
                {groupLabel}
              </h3>
              
              <div className="space-y-2">
                {groupTransactions?.map((transaction) => {
                  const display = getTransactionDisplay(transaction);
                  
                  return (
                    <div
                      key={transaction?.id}
                      className="bg-card rounded-lg border border-border p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${display?.bgColor}`}>
                          <Icon name={display?.icon} size={18} className={display?.color} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-foreground truncate">
                              {transaction?.description}
                            </p>
                            <p className={`font-bold ${display?.color}`}>
                              {transaction?.amount > 0 ? '+' : ''}{loyaltyService?.formatTokens(transaction?.amount)}
                            </p>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                              {loyaltyService?.getTransactionTypeLabel(transaction?.transaction_type)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(transaction?.created_at)}
                            </p>
                          </div>
                          
                          {/* Additional metadata */}
                          {transaction?.metadata && Object.keys(transaction?.metadata)?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {transaction?.metadata?.activity_type && (
                                <span className="inline-flex px-2 py-1 bg-muted rounded-full text-xs text-muted-foreground">
                                  {transaction?.metadata?.activity_type}
                                </span>
                              )}
                              {transaction?.metadata?.duration_minutes && (
                                <span className="inline-flex px-2 py-1 bg-muted rounded-full text-xs text-muted-foreground">
                                  {transaction?.metadata?.duration_minutes}min
                                </span>
                              )}
                              {transaction?.metadata?.rule_applied && (
                                <span className="inline-flex px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                                  {transaction?.metadata?.rule_applied}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Load More */}
      {hasMore && !loading && (
        <div className="text-center">
          <button
            onClick={() => loadTransactions()}
            className="px-4 py-2 border border-border rounded-lg bg-card text-foreground hover:bg-muted/50 transition-colors"
          >
            Load More Transactions
          </button>
        </div>
      )}
      {/* Loading State */}
      {loading && transactions?.length === 0 && (
        <div className="space-y-4">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                    <div className="h-4 bg-muted rounded w-16"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="h-3 bg-muted rounded w-1/3"></div>
                    <div className="h-3 bg-muted rounded w-20"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Loading More */}
      {loading && transactions?.length > 0 && (
        <div className="text-center py-4">
          <div className="inline-flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">Loading more transactions...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;